import { DEFAULT_RATES, type ShopRates } from './settings';
import type { CurrencyCode } from './types';

/**
 * What a currency *is* — its label, symbol and decimal places. Static.
 *
 * The exchange rate is deliberately not here: it is a shop setting the owner
 * edits, and a mutable module-level rate would be a hidden global that breaks
 * tests and any future server rendering. Rates travel as an argument.
 */
export interface CurrencyMeta {
  code: CurrencyCode;
  /** Lao label, e.g. ກີບ */
  label: string;
  sym: string;
  dec: number;
}

export const CURRENCY_META: CurrencyMeta[] = [
  { code: 'LAK', label: 'ກີບ', sym: '₭', dec: 0 },
  { code: 'THB', label: 'ບາດ', sym: '฿', dec: 2 },
  { code: 'USD', label: 'ໂດລາ', sym: '$', dec: 2 },
];

export function meta(code: CurrencyCode): CurrencyMeta {
  return CURRENCY_META.find((c) => c.code === code) ?? CURRENCY_META[0]!;
}

/** LAK per one unit of `code`. Always 1 for LAK itself. */
export function rateOf(code: CurrencyCode, rates: ShopRates): number {
  if (code === 'LAK') return 1;
  return rates[code] ?? DEFAULT_RATES[code];
}

/** Converts a foreign amount into integer kip — the only form ever stored. */
export function toKip(amountForeign: number, code: CurrencyCode, rates: ShopRates): number {
  return Math.round(amountForeign * rateOf(code, rates));
}

export interface CurrencyDef extends CurrencyMeta {
  /** How many LAK one unit of this currency is worth. LAK itself is 1. */
  rate: number;
}

/**
 * @deprecated Rates are a shop setting now — read `shop.rates` and use
 * `CURRENCY_META` / `rateOf` / `fmtIn`. Kept so the migration could land in
 * stages; remove once no screen imports it.
 */
export const CURRENCIES: CurrencyDef[] = CURRENCY_META.map((m) => ({
  ...m,
  rate: rateOf(m.code, DEFAULT_RATES),
}));

/** @deprecated See `CURRENCIES`. Use `meta(code)` plus `rateOf(code, rates)`. */
export function currency(code: CurrencyCode): CurrencyDef {
  return CURRENCIES.find((c) => c.code === code) ?? CURRENCIES[0]!;
}

/** 18500000 → "18,500,000" */
export function fmt(n: number | undefined | null): string {
  return (n ?? 0).toLocaleString('en-US');
}

/** Converts a LAK amount into `code` and formats it with that currency's decimals. */
export function fmtIn(
  amountLak: number,
  code: CurrencyCode,
  rates: ShopRates = DEFAULT_RATES,
): string {
  const c = meta(code);
  if (c.code === 'LAK') return fmt(amountLak);
  return (amountLak / rateOf(code, rates)).toLocaleString('en-US', {
    minimumFractionDigits: c.dec,
    maximumFractionDigits: c.dec,
  });
}

/** 2450000 → "2,450k" — the dense form used in stat tiles. */
export function fmtK(n: number): string {
  if (Math.abs(n) < 1000) return fmt(n);
  return `${fmt(Math.round(n / 1000))}k`;
}

/** Quantities may be fractional for bulk units (0.5 ໂຕນ); drop the trailing ".0". */
export function fmtQty(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

/** Guards against float drift from repeated 0.5 steps. */
export function roundQty(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Parses typed text into integer kip, or null if it is not a usable amount.
 *
 * Never call `Number()` or `parseFloat()` on a money field directly:
 * `parseFloat("55,000")` is 55, which is a 999x error that looks like a
 * successful parse. Group separators are stripped here instead.
 *
 * Kip has no minor unit, so a fractional amount is rejected rather than
 * rounded — silently turning 55.5 into 56 is the kind of quiet edit that money
 * handling must not do.
 */
export function parseKip(raw: string): number | null {
  const cleaned = raw.replace(/[\s,'  ]/g, '');
  if (!/^\d+$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isSafeInteger(n) ? n : null;
}

/**
 * Parses typed text into a quantity, or null. Bulk units allow halves (0.5 ໂຕນ).
 *
 * A comma is treated as the decimal point, not a group separator: Lao and Thai
 * Android keyboards put ',' on the decimal key, so "0,5" is what a shopkeeper
 * actually types for half a ton. Without this it parses to NaN and the cart
 * line is dropped with no error shown.
 */
export function parseQty(raw: string): number | null {
  const cleaned = raw.trim().replace(/[\s'  ]/g, '').replace(',', '.');
  if (!/^\d*\.?\d+$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return null;
  return roundQty(n);
}

/** 2026-07-31 → "31/07/2026" */
export function fmtDate(d: Date): string {
  const p = (v: number) => String(v).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** 2026-07-31 → "31/07" — the short form on invoices. */
export function fmtDateShort(d: Date): string {
  const p = (v: number) => String(v).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}`;
}
